import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

import PolicyFormDialog from './PolicyFormDialog'; // Import the new dialog
import DocumentSelector from '../common/DocumentSelector';
import { getAuthHeader } from '../../utils/authUtils'; // Assuming auth utils exist

// Define policy types (could also be fetched or passed as props)
const POLICY_TYPES = [
  'Commercial General Liability',
  'Commercial Auto Liability',
  'Cargo Insurance',
  'On-Hook Coverage',
  'Garagekeepers Legal Liability',
  "Workers' Compensation",
  'Professional Liability',
  'Business Interruption Insurance',
  'Other'
];

function InsuranceTab({ application, onUpdate, isReadOnly }) {
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // For general success messages
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState(null); // Policy being edited or null for add
  // Which policy row has its COI version selector open
  const [expandedPolicyId, setExpandedPolicyId] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const applicationId = application?._id;

  // A replaced COI is archived rather than deleted, so each policy carries its
  // current file plus a history stack. Current first, then newest replaced.
  const policyVersions = (policy) => {
    const history = (policy.documentHistory || [])
      .filter((h) => h && h.path)
      .slice()
      .sort((a, b) => new Date(b.replacedAt || b.uploadedAt || 0) - new Date(a.replacedAt || a.uploadedAt || 0));
    return [
      ...(policy.documentPath
        ? [{ path: policy.documentPath, current: true, uploadedAt: policy.documentUploadedAt }]
        : []),
      ...history.map((h) => ({ ...h, current: false }))
    ];
  };

  // Shape a stored policy for display, keeping every stored field so a later
  // edit or version delete has the full record to work from.
  const mapPolicyForDisplay = (policy) => ({
    ...policy,
    policyType: policy.policyType || 'Commercial General Liability',
    companyName: policy.companyName || 'N/A',
    policyNumber: policy.policyNumber || 'N/A',
    documentHistory: policy.documentHistory || [],
  });

  // Replace one policy in the list with the server's latest copy of it
  const replacePolicy = (updated) => {
    if (!updated || !updated._id) return;
    setPolicies((prev) => prev.map((p) => (String(p._id) === String(updated._id) ? mapPolicyForDisplay(updated) : p)));
  };

  // --- Data Fetching ---
  const fetchPolicies = useCallback(async () => {
    if (!applicationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/applications/${applicationId}/insurance-policies`, {
        headers: getAuthHeader(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load insurance policies.');
      }
      setPolicies((data.policies || []).map(mapPolicyForDisplay));
    } catch (err) {
      console.error("Error loading insurance policies:", err);
      setError(err.message || 'Failed to load insurance data.');
      setPolicies([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, applicationId]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // --- Dialog Management ---
  const handleAddClick = () => {
    setCurrentPolicy({ _id: 'new-policy' }); // Set ID for new policy
    setDialogOpen(true);
    setError(null); // Clear previous errors
    setSuccess(null);
  };

  const handleEditClick = (policy) => {
    setCurrentPolicy(policy);
    setDialogOpen(true);
    setError(null); // Clear previous errors
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentPolicy(null);
    // Optionally clear error/success messages when closing dialog
    // setError(null);
    // setSuccess(null);
  };

  // --- CRUD Operations ---
  const handleSavePolicy = async (policyData, coiFile) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const isEdit = policyData._id && policyData._id !== 'new-policy';

      // Create the policy object
      const newPolicyData = {
        policyType: policyData.policyType,
        companyName: policyData.companyName,
        policyNumber: policyData.policyNumber,
        coverageAmount: policyData.coverageAmount,
        contactName: policyData.contactName,
        contactPhone: policyData.contactPhone,
        contactFax: policyData.contactFax,
        contactEmail: policyData.contactEmail,
        effectiveDate: policyData.effectiveDate,
        expirationDate: policyData.expirationDate,
        address: policyData.address,
      };

      // 1. Save the policy record first — the COI upload attaches the file to
      // the policy by its id, so a new policy needs one before the upload.
      const response = await fetch(
        isEdit
          ? `${apiBaseUrl}/api/insurance/${policyData._id}`
          : `${apiBaseUrl}/api/applications/${applicationId}/insurance-policies`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: getAuthHeader(),
          body: JSON.stringify(newPolicyData),
        }
      );
      const savedPolicy = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(savedPolicy.message || 'Failed to save insurance information.');
      }
      let latestPolicy = savedPolicy;

      // 2. Upload the COI file (if provided) and attach it to the saved policy
      if (coiFile) {
        const coiFormData = new FormData();
        coiFormData.append('coi', coiFile);
        coiFormData.append('policyId', savedPolicy._id || policyData._id);
        coiFormData.append('policyType', policyData.policyType);
        const token = localStorage.getItem('token');
        const uploadResponse = await fetch(`${apiBaseUrl}/api/applications/${applicationId}/upload-coi`, {
          method: 'POST',
          // No Content-Type header — the browser sets the multipart boundary
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: coiFormData,
        });
        const uploadResult = await uploadResponse.json().catch(() => ({}));
        if (!uploadResponse.ok) {
          throw new Error(`Policy details were saved, but the COI upload failed: ${uploadResult.message || uploadResponse.statusText}`);
        }
        if (uploadResult.policy) latestPolicy = uploadResult.policy;
      }

      setSuccess(
        coiFile
          ? `Insurance policy ${isEdit ? 'updated' : 'added'} successfully. Any previous COI is still available in the version selector.`
          : `Insurance policy ${isEdit ? 'updated' : 'added'} successfully.`
      );
      handleCloseDialog();

      // Show the saved result immediately rather than waiting on a refetch
      if (isEdit) {
        replacePolicy(latestPolicy);
      } else if (latestPolicy && latestPolicy._id) {
        setPolicies((prev) => [...prev, mapPolicyForDisplay(latestPolicy)]);
      } else {
        await fetchPolicies();
      }

      // Notify parent component to refresh application data
      if (onUpdate) {
        onUpdate(newPolicyData);
      }

    } catch (err) {
      console.error("Save Policy Error:", err);
      setError(err.message || `An error occurred while ${policyData._id && policyData._id !== 'new-policy' ? 'updating' : 'adding'} the insurance policy.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Email the applicant a no-login link to upload the COI for one policy
  const [requestingCoi, setRequestingCoi] = useState(false);
  // One outstanding link per policy type: { policyType, requestedAt }[] plus the last send result
  const [coiRequest, setCoiRequest] = useState({ requests: [], error: null, sentFor: null });
  useEffect(() => {
    setCoiRequest({ requests: application?.coiRequests || [], error: null, sentFor: null });
  }, [application?.coiRequests]);

  const handleRequestCoi = async (policy) => {
    setRequestingCoi(true);
    setCoiRequest((prev) => ({ ...prev, error: null, sentFor: null }));
    try {
      const response = await fetch(`${apiBaseUrl}/api/applications/${application._id}/request-coi`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ policyType: policy.policyType })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to send the COI request.');
      setCoiRequest({ requests: data.coiRequests || [{ policyType: policy.policyType, requestedAt: data.coiRequestDate }], error: null, sentFor: policy.policyType });
      setSuccess(data.message || 'COI request sent.');
    } catch (err) {
      setCoiRequest((prev) => ({ ...prev, error: err.message, sentFor: null }));
    } finally {
      setRequestingCoi(false);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    // Optional: Add confirmation dialog here
    if (!window.confirm('Are you sure you want to delete this insurance policy?')) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/insurance/${policyId}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete insurance policy.');
      }

      setSuccess('Insurance policy deleted successfully.');

      // Reflect the deletion immediately rather than racing the parent refetch
      setPolicies((prev) => prev.filter((p) => String(p._id) !== String(policyId)));

      // Notify parent component to refresh application data
      if (onUpdate) {
        onUpdate({ deletedPolicyId: policyId });
      }

    } catch (err) {
      console.error("Delete Policy Error:", err);
      setError(err.message || 'An error occurred while deleting the insurance policy.');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove one archived (or current) COI file from a policy's version stack
  const handleDeleteVersion = async (policy, path) => {
    const response = await fetch(`${apiBaseUrl}/api/insurance/${policy._id}/document-versions`, {
      method: 'DELETE',
      headers: getAuthHeader(),
      body: JSON.stringify({ path }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete document version');
    }
    if (data.policy) replacePolicy(data.policy);
    else await fetchPolicies();
    return data;
  };

  // --- Rendering ---
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Insurance Policies
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={isReadOnly || isLoading}
        >
          Add New Policy
        </Button>
      </Box>

      {isLoading && policies.length === 0 && ( // Show loader only on initial load
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && policies.length === 0 && !error && (
        <Typography sx={{ textAlign: 'center', p: 3 }}>
          No insurance policies have been added yet.
        </Typography>
      )}

      {policies.length > 0 && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="insurance policies table">
            <TableHead>
              <TableRow>
                <TableCell>Policy Type</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Policy #</TableCell>
                <TableCell>Coverage</TableCell>
                <TableCell>Expiration Date</TableCell>
                <TableCell align="center">COI</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map((policy) => (
                <React.Fragment key={policy._id}>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {policy.policyType}
                  </TableCell>
                  <TableCell>{policy.companyName}</TableCell>
                  <TableCell>{policy.policyNumber}</TableCell>
                  <TableCell>
                    {policy.coverageAmount ? `$${Number(policy.coverageAmount).toLocaleString()}` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {policy.expirationDate ? new Date(policy.expirationDate).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {policyVersions(policy).length > 0 ? (
                      <Tooltip title={
                        policyVersions(policy).length > 1
                          ? `${policyVersions(policy).length} versions on file — open the selector`
                          : 'View COI'
                      }>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => setExpandedPolicyId(
                            expandedPolicyId === policy._id ? null : policy._id
                          )}
                        >
                          {policyVersions(policy).length > 1
                            ? `${policyVersions(policy).length} versions`
                            : 'View'}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="textSecondary">None</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit Policy">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(policy)}
                        disabled={isReadOnly || isLoading}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Policy">
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePolicy(policy._id)}
                        disabled={isReadOnly || isLoading}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {expandedPolicyId === policy._id && (
                  <TableRow key={`${policy._id}-docs`}>
                    <TableCell colSpan={7} sx={{ backgroundColor: 'action.hover' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {policy.policyType} — certificates of insurance
                      </Typography>
                      <DocumentSelector
                        versions={policyVersions(policy)}
                        label="COI"
                        isReadOnly={isReadOnly}
                        onDelete={async (v) => {
                          await handleDeleteVersion(policy, v.path);
                          if (onUpdate) onUpdate();
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Render the Dialog */}
      <PolicyFormDialog
        open={dialogOpen}
        policyData={currentPolicy}
        policyTypes={POLICY_TYPES} // Pass the defined types
        onClose={handleCloseDialog}
        onSave={handleSavePolicy} // Pass the save handler
        onRequestCoi={isReadOnly ? undefined : handleRequestCoi}
        requestingCoi={requestingCoi}
        coiRequest={coiRequest}
        applicantEmail={application?.email}
      />

      {/* Removed the old form and PDF viewer */}
      {/* Removed the old save button for the single form */}

    </Box>
  );
}

export default InsuranceTab;
